import { useRequestsHandlerData } from './useRequestsHandlerData';
import { useEffect, useRef, useState } from 'react';
import { useContent } from '../redux/reducer/content';
import createUniqueHash from '../utils/createUniqueHash';

const fieldsNames = [
  ['score', ['score', 'points']],
  ['money', ['coins']],
];

export const useGameControls = ({ isInfinity, gameType, wrapper, gameData, logs, callbacks }) => {
  const { gameProgressData } = useContent();
  const [isPlaying, setIsPlaying] = useState(true);
  const [boosterCount, setBoosterCount] = useState(0);
  const [timeoutId, setTimeoutId] = useState(2000);
  const gameDataRef = useRef(gameData);

  const getFormattedData = (isGameEnd = false) => {
    const progressData = fieldsNames.reduce(
      (acc, [key, values]) => {
        const necessaryKey = values.find((value) => typeof gameDataRef.current[value] === 'number');
        if (!necessaryKey) return acc;
        return [...acc, [key, gameDataRef.current[necessaryKey]]];
      },
      [...(isGameEnd ? [['is_win', 1]] : [])],
    );

    return Object.fromEntries(progressData);
  };

  const win = () => {
    const strictObject = getFormattedData(true);
    const hash = createUniqueHash(gameProgressData.session, strictObject);
    callbacks?.gameEnd({ is_win: 1, ...strictObject, logs, booster_count: boosterCount, hash });
  };

  const lose = () => {
    const strictObject = getFormattedData(true);
    callbacks?.gameEnd({ is_win: 0, ...strictObject, booster_count: boosterCount });
  };

  const progress = (data) => {
    callbacks?.sendGameProgress({ ...data, booster_count: boosterCount });
  };

  const sendProgressData = () => {
    const strictObject = getFormattedData();
    const hash = createUniqueHash(gameProgressData.session, strictObject);
    progress({ ...strictObject, hash });
  };

  useRequestsHandlerData([
    {
      address: 'requestsGameStart',
      pending: () => {
        setIsPlaying(false);
        setBoosterCount(0);
      },
      fulfilled: () => setIsPlaying(true),
    },
    {
      address: 'requestsGameEnd',
      pending: () => setIsPlaying(false),
    },
    {
      address: 'requestsGamePause',
      fulfilled: ({ log: { status } }) => setIsPlaying(status !== 'Paused'),
    },
    {
      address: 'requestsSendGameProgress',
      fulfilled: (data) => {
        const newInterval = data?.log?.save_progress_interval;
        if (typeof newInterval === 'number') {
          const milliseconds = newInterval * 1000;
          setTimeoutId(setTimeout(sendProgressData, milliseconds));
        }
      },
      rejected: () => {
        setTimeoutId(setTimeout(sendProgressData, 30000));
      },
    },
  ]);

  useEffect(() => {
    gameDataRef.current = gameData;
  }, [gameData]);

  useEffect(() => {
    if (!isPlaying || Object.values(gameProgressData).some((data) => !data)) return;
    sendProgressData();
  }, [isPlaying, gameProgressData]);

  useEffect(() => {
    if (!isPlaying && timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
  }, [isPlaying, timeoutId]);

  useEffect(() => () => typeof timeoutId === 'number' && clearTimeout(timeoutId), []);

  useEffect(() => {
    if (!wrapper?.eventBus) return;
    const listener = () => setBoosterCount((prev) => ++prev);
    wrapper?.eventBus?.addEventListener('booster:enabled', listener);
    return () => wrapper?.eventBus?.removeEventListener('booster:enabled', listener);
  }, [wrapper?.eventBus]);

  return { win, lose };
};
